import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Seo from '../components/Seo';
import { BLOG_POSTS } from '../data/blogPosts';

const formatDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return '';
  }
};

const Block = ({ block }) => {
  switch (block.type) {
    case 'h2':
      return <h2>{block.text}</h2>;
    case 'h3':
      return <h3>{block.text}</h3>;
    case 'ul':
      return <ul>{block.items.map((it, i) => <li key={i}>{it}</li>)}</ul>;
    case 'ol':
      return <ol>{block.items.map((it, i) => <li key={i}>{it}</li>)}</ol>;
    case 'links':
      return (
        <ul>
          {block.items.map((it, i) => (
            <li key={i}>
              {it.url ? (
                <a href={it.url} target="_blank" rel="noopener noreferrer">{it.label}</a>
              ) : (
                it.label
              )}
            </li>
          ))}
        </ul>
      );
    case 'quote':
      return <blockquote className="border-l-4 border-[#cfe0ef] pl-4 italic text-navy/80 my-5">{block.text}</blockquote>;
    case 'image':
      return <img src={block.src} alt={block.alt || ''} />;
    case 'p':
    default:
      return <p>{block.text}</p>;
  }
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  if (!post) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-32 text-center">
        <h1 className="font-heading text-navy text-3xl">Post not found</h1>
        <Link to="/blog" className="inline-flex items-center gap-2 mt-6 text-[#3d6a9c] hover:text-navy"><ArrowLeft size={16} /> Back to Blog</Link>
      </div>
    );
  }

  return (
    <div>
      <Seo
        title={post.title}
        description={post.excerpt || post.title}
        path={`/blog/${post.slug}`}
        image={post.image || undefined}
        type="article"
      />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-[#3d6a9c] hover:text-navy mb-8"><ArrowLeft size={16} /> Back to Blog</Link>
        <div className="flex items-center gap-2 text-xs font-heading font-semibold tracking-wide text-[#3d6a9c] uppercase">
          <span>{post.category}</span>
          {post.date && <><span className="text-border">•</span><span className="text-muted-foreground normal-case font-normal tracking-normal">{formatDate(post.date)}</span></>}
        </div>
        <h1 className="mt-3 font-heading font-light text-navy text-3xl sm:text-4xl leading-tight">{post.title}</h1>
        {(post.author || post.readTime) && (
          <p className="mt-3 text-sm text-muted-foreground">
            {post.author}
            {post.author && post.readTime ? ' \u00b7 ' : ''}
            {post.readTime}
            {post.updated ? ` \u00b7 Updated ${formatDate(post.updated)}` : ''}
          </p>
        )}
        {post.image && (
          <div className="mt-8 rounded-2xl overflow-hidden shadow-md">
            <img src={post.image} alt={post.title} className="w-full object-cover" />
          </div>
        )}
        <div className="article-content mt-8">
          {(post.content || []).map((block, i) => <Block key={i} block={block} />)}
        </div>
      </article>
    </div>
  );
};

export default BlogPost;
